/**
 * Runtime gate for third-party web payment buttons (Stripe, PayPal, any
 * hosted checkout).
 *
 * Google Play policy prohibits third-party payment processing for digital
 * goods, so the Android Capacitor build must use Google Play Billing
 * exclusively. This gate is the only suppressor of web payment UI inside the
 * native shell, which is why it FAILS CLOSED: any native indicator at all
 * (Capacitor object present, Cordova, capacitor:/ionic:/file: scheme, Android
 * WebView UA, missing window/document) hides the buttons. Web payment UI is
 * shown only when every signal says "real browser."
 *
 * Always gate payment button rendering on this helper:
 *   {showWebPaymentButtons() && <Button onClick={...}>Subscribe</Button>}
 *
 * A missed detection is a Play Store policy violation, so when adding a new
 * payment provider, reuse this gate rather than writing a second copy of it.
 */
export function showWebPaymentButtons(): boolean {
  // Defensive: if there is no window/document, we are not in a real browser
  // (SSR, worker, native bridge during boot) — hide.
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const g = window as unknown as {
    Capacitor?: { isNativePlatform?: () => boolean; platform?: string };
    cordova?: unknown;
    location?: { protocol?: string };
  };

  // 1. Capacitor bridge object present at all → assume native shell.
  //    @capacitor/core only injects window.Capacitor in the native runtime
  //    (the JS bundle does not create it on plain web). Be strict: any
  //    presence of the object — even if isNativePlatform throws or is
  //    missing — counts as "probably native."
  if (g.Capacitor !== undefined) {
    try {
      const isNative = g.Capacitor.isNativePlatform?.();
      // Explicit false is the only way to stay visible from here; anything
      // else (true, undefined, throw) hides the buttons.
      if (isNative !== false) return false;
    } catch {
      return false;
    }
    // Also check the platform field if it's set to something other than "web".
    if (g.Capacitor.platform && g.Capacitor.platform !== "web") {
      return false;
    }
  }

  // 2. Cordova bridge → native wrapper.
  if (g.cordova !== undefined) return false;

  // 3. Custom capacitor: / ionic: / file: schemes → native shell.
  const proto = g.location?.protocol ?? "";
  if (proto === "capacitor:" || proto === "ionic:" || proto === "file:") {
    return false;
  }

  // 4. Android WebView UA heuristic (belt + suspenders for in-app browsers
  //    that didn't inject Capacitor). "; wv)" is the Android WebView marker.
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/Android.*;\s*wv\)/.test(ua)) return false;

  return true;
}
